import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import WizardShell from "./WizardShell";
import VoiceTextarea from "@/components/VoiceTextarea";
import SpeakButton from "@/components/SpeakButton";
import { generateAlign } from "@/lib/api";

export default function AlignStep({
  attention,
  witness,
  contextText,
  refinedProblem,
  onRefinedChange,
  onBack,
  onNext,
}) {
  const [statements, setStatements] = useState([]);
  const [selectedIdx, setSelectedIdx] = useState(-1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const didFetchRef = useRef(false);

  const fetchStatements = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await generateAlign({ attention, witness, context: contextText || undefined });
      setStatements(res.problem_statements || []);
      setSelectedIdx(-1);
    } catch (e) {
      const msg =
        e?.response?.data?.detail || e?.message || "Something went wrong.";
      setError(msg);
      toast.error("Couldn't generate statements", { description: msg });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (didFetchRef.current) return;
    didFetchRef.current = true;
    fetchStatements();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const select = (idx) => {
    setSelectedIdx(idx);
    onRefinedChange(statements[idx] || "");
  };

  const canContinue = refinedProblem.trim().length > 8;

  return (
    <WizardShell
      pillLabel="Step 3 · Align"
      pillColor="#4aa0a4"
      headline="Three honest framings of the problem."
      aiOpening={
        "I read what you wrote. Here are three ways to name the problem — each pulls in a different direction.\n\nPick the one that lands. You can edit it before moving on."
      }
      testid="align-step"
      footer={
        <>
          <button
            type="button"
            className="weave-btn-outline"
            onClick={onBack}
            data-testid="align-back-btn"
          >
            ← Back
          </button>
          <button
            type="button"
            className="weave-btn-primary"
            onClick={onNext}
            disabled={!canContinue}
            data-testid="align-next-btn"
          >
            Continue to Kata
          </button>
        </>
      }
    >
      {loading && (
        <div
          className="flex items-center gap-3 text-slate-500"
          data-testid="align-loading"
        >
          <span className="weave-spinner" />
          Listening, then drafting three framings…
        </div>
      )}

      {error && !loading && (
        <div className="weave-card-soft" data-testid="align-error">
          <p className="text-slate-700">
            I couldn't draft your problem statements just now.
          </p>
          <p className="text-sm text-slate-500 mt-2">{error}</p>
          <button
            type="button"
            className="weave-btn-primary mt-4"
            onClick={fetchStatements}
            data-testid="align-retry-btn"
          >
            Try again
          </button>
        </div>
      )}

      {!loading && !error && statements.length > 0 && (
        <div className="space-y-3" data-testid="align-statements">
          <div className="flex justify-end">
            <SpeakButton
              text={statements
                .map((s, i) => `Option ${i + 1}. ${s}`)
                .join(". ")}
              label="Read all three options aloud"
              testid="align-speak-all-btn"
            />
          </div>
          {statements.map((s, idx) => {
            const isSelected = selectedIdx === idx;
            return (
              <motion.button
                key={idx}
                type="button"
                onClick={() => select(idx)}
                whileHover={{ y: -2 }}
                transition={{ duration: 0.2 }}
                className="block w-full text-left rounded-2xl px-6 py-5 transition-colors"
                style={{
                  background: isSelected ? "rgba(74,160,164,0.06)" : "#ffffff",
                  border: isSelected
                    ? "1px solid #4aa0a4"
                    : "1px solid #e2e8f0",
                }}
                data-testid={`align-option-${idx}`}
              >
                <div className="flex items-start gap-4">
                  <span
                    className="flex-shrink-0 mt-1 flex items-center justify-center text-xs font-medium"
                    style={{
                      width: 24,
                      height: 24,
                      borderRadius: 9999,
                      border: isSelected
                        ? "1px solid #4aa0a4"
                        : "1px solid #cbd5e1",
                      background: isSelected ? "#4aa0a4" : "#ffffff",
                      color: isSelected ? "#ffffff" : "#94a3b8",
                    }}
                  >
                    {idx + 1}
                  </span>
                  <span className="text-slate-700 leading-relaxed">{s}</span>
                </div>
              </motion.button>
            );
          })}
        </div>
      )}

      {selectedIdx >= 0 && !loading && (
        <div className="mt-8">
          <label className="block text-sm text-slate-500 mb-2">
            Refine the wording until it sounds true
          </label>
          <VoiceTextarea
            rows={3}
            value={refinedProblem}
            onChange={onRefinedChange}
            ariaLabel="Refine your problem statement"
            testid="align-refined-input"
            micTestid="align-refined-mic"
          />
        </div>
      )}

      {!loading && !error && (
        <div className="mt-6">
          <button
            type="button"
            className="weave-btn-ghost text-sm"
            onClick={fetchStatements}
            data-testid="align-regenerate-btn"
          >
            ↻ Generate three new framings
          </button>
        </div>
      )}
    </WizardShell>
  );
}
